<template>
  <div class="px-3 pt-2 h-full overflow-auto pb-5">
    <div>
      <VariableSelect
        v-model="statementPayload.payload.variable"
        label="Assign to Variable"
        :filter="lhsVariableFilter"
        class="flex-grow"
        :placeholder="$t('evaluate.select_variable')"
        @update:modelValue="onVariableInput"
        @option:selected="onVariableOptionSelected"
      >
      </VariableSelect>
      <div
        class="flex"
        :class="{
          'flex-wrap': statementPayload.payload.assignment_type === 'value' && getInputType(statementPayload.payload.variable) === 'string' && statementPayload.payload.dataType === 'Text',
        }"
      >
        <FormSelect
          v-model="statementPayload.payload.assignment_type"
          :label="$t('evaluate.assign')"
          :reduce="(option) => option.value"
          class="w-150 mt-3 mr-3 flex-shrink-0"
          :searchable="false"
          :clearable="false"
          appendToBody
          :options="[
            { label: $t('variable'), value: 'variable' },
            { label: $t('value'), value: 'value' },
          ]"
          @option:selected="onValueTypeChanged"
        ></FormSelect>

        <template v-if="statementPayload.payload.assignment_type === 'value'">
          <div class="w-[180px] mt-3 pr-3 flex-shrink-0">
            <FormSelect
              v-model="statementPayload.payload.dataType"
              :label="$t('data_type')"
              :options="getDataTypes"
              :reduce="(type) => type.value"
              :clearable="false"
              :searchable="false"
              appendToBody
              @option:selected="statementPayload.payload.value = null"
            />
          </div>
        </template>
        <template v-if="statementPayload.payload.assignment_type === 'value' && isUserDataType(statementPayload.payload.dataType)">
          <div class="w-full mt-3">
            <FormSelect
              :label="$t('value')"
              class="w-full"
              key="static"
              :clearable="false"
              :modelValue="statementPayload.payload.value"
              :reduce="(option) => option.value"
              :options="getSelectableParameters(statementPayload.payload.dataType)"
              @option:selected="onStaticFieldsValueInput"
            ></FormSelect>
          </div>
        </template>
        <template
          v-else-if="statementPayload.payload.assignment_type === 'value' && isSystemType(statementPayload.payload.dataType) && !['Text', 'DataTable'].includes(statementPayload.payload.dataType)"
        >
          <div class="w-full mt-3 overflow-hidden">
            <FormLabel :label="$t('value')" />
            <FormSelect
              v-if="statementPayload.payload.dataType === 'YesNo'"
              v-model="statementPayload.payload.value"
              :options="['Yes', 'No']"
              appendToBody
              class="w-full"
              @update:modelValue="emitPayload"
            />
            <Input v-else-if="statementPayload.payload.dataType === 'Number'" type="number" v-model="statementPayload.payload.value" class="w-full" @update:modelValue="emitPayload" />
          </div>
        </template>
        <template v-else-if="statementPayload.payload.assignment_type === 'value' && !isSystemType(statementPayload.payload.dataType)">
          <FormSelect :label="$t('value')" class="w-full mt-3" v-model="statementPayload.payload.value" :options="getDataTypeFields(statementPayload.payload.dataType)"> </FormSelect>
        </template>
        <template v-if="statementPayload.payload.assignment_type === 'variable'">
          <div class="flex mt-3 w-full">
            <div class="flex-grow min-w-200 flex-shrink-0">
              <span class="text-gray-600 text-12 mb-1">{{ $t('value') }}</span>
              <VariableSelect
                v-model="statementPayload.payload.value"
                :filter="rhsVariableFilter"
                class="w-full"
                :placeholder="$t('evaluate.select_variable')"
                @update:modelValue="emitPayload"
                @option:selected="onRHSVariableOptionSelected"
              >
              </VariableSelect>
            </div>
          </div>
        </template>
      </div>
      <div class="" v-if="statementPayload.payload.assignment_type === 'value' && isSystemType(statementPayload.payload.dataType) && ['Text', 'DataTable'].includes(statementPayload.payload.dataType)">
        <div class="w-full mt-3 overflow-hidden flex-wrap">
          <FormLabel class="block text-12 mb-1" :label="$t('value')" />
          <div v-if="statementPayload.payload.dataType === 'Text'" class="h-400">
            <PhraseEditor multiline :items="variableList" v-model="statementPayload.payload.value" class="w-full" @update:modelValue="emitPayload" />
          </div>
          <div v-else-if="statementPayload.payload.dataType === 'DataTable'" class="h-400">
            <div class="flex pt-2 pb-1 flex-shrink-0 hidden">
              <div
                class="w-100 select-none cursor-pointer h-6 flex items-center justify-center border-purple-200 border-b border-r"
                :class="{ 'text-gray-900': tab === 'editor', 'text-gray-600': tab !== 'editor' }"
                :style="{ 'border-bottom-color': tab === 'editor' ? 'transparent' : '' }"
                @click="tab = 'editor'"
              >
                {{ $t('editor') }}
              </div>
              <div
                class="w-100 select-none cursor-pointer h-6 flex items-center justify-center border-purple-200 border-b"
                :class="{ 'text-gray-900': tab === 'preview', 'text-gray-600': tab !== 'preview' }"
                :style="{ 'border-bottom-color': tab === 'preview' ? 'transparent' : '' }"
                @click="tab = 'preview'"
              >
                {{ $t('preview') }}
              </div>
            </div>

            <div class="w-full h-full relative border border-solid border-stroke" v-show="tab === 'editor'">
              <MonacoEditor
                width="100%"
                height="100%"
                class="flex-grow h-full"
                v-model:value="statementPayload.payload.value"
                language="json"
                :options="resultEditorOptions"
                @mount="monacoMounted"
                @update:value="emitPayload"
              ></MonacoEditor>
              <div class="absolute bottom-0 right-6 bg-white text-gray-600 text-13 p-1 cursor-pointer" @click="formatEditor">{{ $t('format_code') }}</div>
            </div>

            <div v-show="tab === 'preview'">
              {{ dataTableValue }}
              <DataTable v-if="Array.isArray(dataTableValue)" :value="dataTableValue" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import MonacoEditor from '@guolao/vue-monaco-editor';
import uuidv4 from '@/helpers/uuid';
import { MONACO_EDITOR_DEFAULTS } from '@/constants';
import debounce from '../../../helpers/debounce';

export default {
  name: 'StatementConfig',
  components: { MonacoEditor },
  props: {
    item: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      statementPayload: {
        type: 'logic-statement',
        payload: {
          variable: null,
          assignment_type: 'value',
          value: '',
          dataType: null,
        },
      },
      resultEditorOptions: MONACO_EDITOR_DEFAULTS,
      tab: 'editor',
      editor: null,
    };
  },
  computed: {
    ...mapState(['projectData']),
    projectId() {
      return this.$route.params.projectId;
    },
    dataTableValue() {
      try {
        return JSON.parse(this.statementPayload.payload.value);
      } catch {
        return [];
      }
    },
    variableList() {
      return Object.keys(this.variables)
        .map((v) => {
          return {
            id: uuidv4(),
            value: v,
          };
        })
        .sort((a, b) => a.value.toLowerCase().localeCompare(b.value.toLowerCase()));
    },
    variables() {
      return this.projectData?.[this.projectId]?.variables || {};
    },
    entities() {
      return this.projectData?.[this.projectId]?.datatypes || {};
    },
    valueType() {
      return this.statementPayload.payload.assignment_type;
    },
    variableDataType() {
      return this.variables?.[this.statementPayload.payload.variable]?.dataType;
    },
    showValueAssignmentType() {
      return this.variableDataType === 'YesNo' || ['string', 'number', 'datatable'].includes(this.getInputType(this.statementPayload.payload.variable));
    },
    getDataTypes() {
      let dataTypes = [];
      if (this.variables?.[this.statementPayload.payload.variable]?.dataType) {
        if (this.variables?.[this.statementPayload.payload.variable]?.dataType === 'Text') {
          dataTypes = Object.keys(this.entities)
            .filter((dataType) => !this.entities[dataType].tags.includes('system'))
            .filter((dataType) => !['DataTable', 'Chart'].includes(dataType));

          if (
            this.statementPayload.payload.variable &&
            this.variables?.[this.statementPayload.payload.variable]?.dataType &&
            !dataTypes.includes(this.variables?.[this.statementPayload.payload.variable]?.dataType)
          ) {
            dataTypes.push(this.variables[this.statementPayload.payload.variable].dataType);
          }
        } else {
          dataTypes.push(this.variables[this.statementPayload.payload.variable].dataType);
        }
      } else {
        dataTypes = Object.keys(this.entities).filter((dataType) => !['Chart', 'PDF', 'Image', 'Date', 'Schedule'].includes(dataType));
      }
      return dataTypes.map((dataType) => ({ label: dataType, value: dataType })).sort((a, b) => a.label - b.label);
    },
  },
  methods: {
    onVariableOptionSelected(e) {
      if (this.statementPayload.payload.assignment_type === 'value' || (this.statementPayload.payload.assignment_type === 'variable' && e?.variable?.dataType)) {
        if (this.statementPayload.payload.dataType !== e.variable.dataType) {
          this.statementPayload.payload.value = null;
        }
        this.statementPayload.payload.dataType = e.variable.dataType;
      }
      this.emitPayload();
    },
    onRHSVariableOptionSelected(e) {
      if (this.statementPayload.payload.assignment_type === 'value' || (this.statementPayload.payload.assignment_type === 'variable' && e?.variable?.dataType)) {
        if (this.statementPayload.payload?.variable && this.variables[this.statementPayload.payload?.variable].dataType !== e.variable.dataType) {
          this.statementPayload.payload.variable = null;
        }
      }
      this.emitPayload();
    },
    onVariableInput(e) {
      if (e === null) {
        this.clearRHS();
        this.emitPayload();
      }
    },
    onStaticFieldsValueInput(field) {
      this.statementPayload.payload.value = field.value;
      this.emitPayload();
    },
    isUserDataType(dataType) {
      return typeof this.entities[dataType]?.fields === 'object';
    },
    onParameterDataTypeInput(index) {
      this.statementPayload.payload.value.arguments[index] = null;
    },
    getParameterDataTypes(index, rawArray) {
      let dataTypes = [];
      if (this.selectedFunction) {
        if (this.selectedFunction.arguments[index].type.split('|').includes('Text')) {
          dataTypes = Object.keys(this.entities)
            .filter((dataType) => !this.entities[dataType].tags.includes('system'))
            .filter((dataType) => !['DataTable', 'Chart'].includes(dataType));
        }
        const argTypes = this.selectedFunction.arguments[index].type.split('|');
        if (this.statementPayload.payload.value.argumentsData[index].type === 'static') {
          argTypes.forEach((argType) => {
            if (!dataTypes.includes(argType)) dataTypes.push(argType);
          });
        } else {
          argTypes.forEach((argType) => {
            if (!dataTypes.includes(argType)) dataTypes.push(argType);
          });
        }
      }
      if (rawArray) {
        return dataTypes;
      }
      return dataTypes.map((dataType) => ({ label: dataType, value: dataType })).sort((a, b) => a.label - b.label);
    },
    getSelectableParameters(dataType) {
      if (dataType) {
        const fields = this.entities[dataType].fields || {};
        return Object.keys(fields).map((field) => ({ label: field, value: field }));
      }
      return [];
    },
    formatEditor() {
      this.editor.getAction('editor.action.formatDocument').run();
    },
    monacoMounted(editor, monaco) {
      this.editor = editor;
      // validation settings
      monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
        noSemanticValidation: true,
        noSyntaxValidation: false,
      });
      // compiler options
      monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
        target: monaco.languages.typescript.ScriptTarget.ES6,
        allowNonTsExtensions: true,
      });
    },
    isSystemType(dataType) {
      return this.entities?.[dataType]?.tags.includes('system');
    },
    getDataTypeFields(dataType) {
      const fields = this.entities?.[dataType]?.fields || {};
      return Object.keys(fields).map((field) => ({ label: field, value: field }));
    },
    getInputType(variableName) {
      return this.entities?.[this.variables?.[variableName]?.dataType]?.dataType;
    },
    lhsVariableFilter(variable) {
      if (this.statementPayload.payload.dataType) {
        return this.statementPayload.payload.dataType === variable.dataType && !variable.tags.includes('readonly');
      }
      return !variable.tags.includes('readonly');
    },
    rhsVariableFilter(variable) {
      if (this.statementPayload.payload.dataType) {
        return this.statementPayload.payload.dataType === variable.dataType;
      }
      return true;
    },
    onValueTypeChanged() {
      this.clearRHS();
      this.emitPayload();
    },
    clearRHS() {
      this.statementPayload.payload.value = '';

      if (this.getDataTypes.length === 1) {
        this.statementPayload.payload.dataType = this.getDataTypes[0].value;
      } else {
        this.statementPayload.payload.dataType = null;
      }
    },
    // eslint-disable-next-line
    debouncedPayload: debounce(function () {
      const statementPayload = JSON.parse(JSON.stringify(this.statementPayload));
      delete statementPayload.payload.dataType;

      this.$emit('update', statementPayload);
    }, 300),
    emitPayload(e) {
      if (e) {
        if (this.statementPayload.payload.value && this.statementPayload.payload.assignment_type === 'variable') {
          if (this.variables[this.statementPayload.payload.value].dataType !== this.variables[e].dataType) {
            this.clearRHS();
          }
        }
      }

      this.validate();
      this.debouncedPayload();
    },
    validate() {
      let valid = true;
      if (this.valueType === 'value') {
        if (this.variableDataType === 'DataTable') {
          try {
            const payload = JSON.parse(this.statementPayload.payload.value);
            if (!Array.isArray(payload)) {
              valid = false;
            }
          } catch (e) {
            valid = false;
          }
        }
      }
      this.$emit('valid', valid);
    },
  },
  watch: {
    item: {
      handler() {
        if (this.item) {
          this.statementPayload = JSON.parse(JSON.stringify(this.item));
          if (this.statementPayload.payload.assignment_type === 'value') {
            if (this.statementPayload.payload.variable) {
              this.statementPayload.payload.dataType = this.variables[this.statementPayload.payload.variable]?.dataType;
            }
          } else if (this.statementPayload.payload.assignment_type === 'variable') {
            if (this.statementPayload.payload.value) {
              this.statementPayload.payload.dataType = this.variables[this.statementPayload.payload.value]?.dataType;
            }
          }
        }
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>
